import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { MembersListDataDocument } from "#app/lib/data";
import type { EntityItemConfig, EntityPoolConfig } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "roles";
const QUERY_VARS = { limit: 1 };

export type UseRolePoolOptions = Omittable<{
  /** Only include roles at or below this hierarchy level */
  maxHierarchy?: number;
  /**
   * Restrict the pool to this set of role IDs. Used to show only the
   * roles the current user is allowed to invite people into. When
   * undefined, no allowlist filter is applied. An empty array filters
   * everything out (e.g. a user with no invite grants).
   */
  allowedRoleIds?: readonly string[];
}>;

/**
 * Fetches workspace roles and returns an EntityPoolConfig.
 *
 * Roles are side-loaded from MembersListDataDocument because no dedicated
 * roles query exists. We pass `limit: 1` to minimize the unused user payload.
 * Apollo deduplicates this if the members table is also mounted.
 */
export function useRolePool(options?: UseRolePoolOptions) {
  const { maxHierarchy, allowedRoleIds } = options ?? {};

  const { data, loading } = useQuery(MembersListDataDocument, {
    variables: QUERY_VARS,
  });

  const allowedSet = useMemo(
    () => (allowedRoleIds ? new Set(allowedRoleIds) : null),
    [allowedRoleIds],
  );

  const items = useMemo<EntityItemConfig[]>(() => {
    if (!data?.roles?.edges) return [];
    return data.roles.edges
      .filter((edge) => edge?.node)
      .filter((edge) => {
        if (maxHierarchy == null) return true;
        return (edge.node.hierarchy ?? 0) >= maxHierarchy;
      })
      .filter((edge) => (allowedSet ? allowedSet.has(edge.node.roleId) : true))
      .map((edge) => {
        const node = edge.node;
        return {
          id: node.roleId,
          title: node.roleName,
          data: { hierarchy: node.hierarchy, isSystem: node.isSystem },
        } satisfies EntityItemConfig;
      })
      .sort((a, b) => ((a.data?.hierarchy as number) ?? 0) - ((b.data?.hierarchy as number) ?? 0));
  }, [data, maxHierarchy, allowedSet]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Roles",
      items,
      hideAvatars: true,
    }),
    [items],
  );

  const defaultRoleId = useMemo(() => {
    const member = items.find(
      (r) => r.title.trim().toLowerCase() === "member",
    );
    if (member) return member.id;
    // Fall back to the first item — when "Member" is filtered out (e.g. a
    // user without invite grants for it) we still want a sensible default.
    return items[0]?.id ?? null;
  }, [items]);

  return { pool, loading, defaultRoleId };
}

export { POOL_ID as ROLE_POOL_ID };
